import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { WeekDay } from 'src/app/api/contracts/menu';
import { WEEK_DAYS } from 'src/constants/date.constants';
import { DaySelectorModel, DaySelectorModelItem } from './day-selector.types';
import moment from 'moment';
import { APP_CONFIG, AppConfig } from 'src/app/config/app-config';
import { WeekIndex } from 'src/app/types/menu';
import { getWeekFirstDay, getWeekLastDay } from 'src/app/utils/date.utils';

@Component({
  selector: 'app-day-selector',
  templateUrl: './day-selector.component.html',
  styleUrls: ['./day-selector.component.scss'],
})
export class DaySelectorComponent implements OnChanges {
  public readonly today: WeekDay;
  public model: DaySelectorModel;

  @Input() firstAvailable: WeekDay = getWeekFirstDay();
  @Input() selectedDay: WeekDay = getWeekFirstDay();
  @Input() displayedDays: WeekDay[] = [];
  @Input() weekIndex: number = 0;

  @Output() daySelected = new EventEmitter<WeekDay>();

  @ViewChild('dayList') dayList!: ElementRef;

  constructor(@Inject(APP_CONFIG) private readonly appConfig: AppConfig) {
    this.today = WEEK_DAYS[moment().weekday()];
    this.model = this.createModel();
  }

  ngOnChanges() {
    this.model = this.createModel();
  }

  onDayClick(item: DaySelectorModelItem, htmlElement: HTMLElement): void {
    if (item.dayName === this.selectedDay) {
      return;
    }
    this.daySelected.next(item.dayName);
    this.scrollToSelectedDay(htmlElement);
  }

  private createModel(): DaySelectorModel {
    const currentDate = moment();
    const daysToAdd = this.weekIndex * 7; // 7 days
    const weekStart = currentDate.startOf('week').add(daysToAdd, 'days');
    return {
      items: WEEK_DAYS.map((dayName, index) => {
        const dayOfTheCurrentWeek = weekStart.clone().add(index, 'day');

        const cantOrderOnThisDay =
          (this.weekIndex === WeekIndex.THIS_WEEK &&
            index <= WEEK_DAYS.indexOf(this.today)) ||
          (this.weekIndex === WeekIndex.THIS_WEEK &&
            index === WEEK_DAYS.indexOf(this.today) + 1 &&
            new Date().getHours() >= this.appConfig.stopReceivingOrdersAt) ||
          (this.weekIndex === WeekIndex.NEXT_WEEK &&
            dayName === getWeekFirstDay() &&
            WEEK_DAYS.indexOf(this.today) >=
              WEEK_DAYS.indexOf(getWeekLastDay()) &&
            new Date().getHours() >= this.appConfig.stopReceivingOrdersAt);
        return {
          dayName,
          disabled: cantOrderOnThisDay || !this.displayedDays.includes(dayName),
          isToday:
            this.weekIndex === WeekIndex.THIS_WEEK &&
            index === WEEK_DAYS.indexOf(this.today),
          dayCalendarNumber: dayOfTheCurrentWeek.date(),
          monthNameI18n: `@common.months.short.${dayOfTheCurrentWeek
            .format('MMM')
            .toLocaleLowerCase()}`,
          dayNameI18n: `@common.days.short.${dayName}`,
        };
      }),
    };
  }

  private scrollToSelectedDay(target: HTMLElement): void {
    const dayElementWidthToExclude = 164;
    const left = target.offsetLeft - dayElementWidthToExclude;
    setTimeout(() => {
      this.dayList.nativeElement.scrollTo({ left, behavior: 'smooth' });
    });
  }
}
